import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import type { NmiConfig } from '../nmi.config';
import {
  CaptureRequest,
  ConfirmPaymentIntentRequest,
  CreatePaymentIntentRequest,
  Payment,
  PaymentIntent,
  Refund,
  RefundRequest,
  SaleWithVaultRequest,
  AuthWithVaultRequest,
} from '../models/nmi.models';

export class NmiPaymentService {
  private readonly base: string;

  constructor(private http: HttpClient, config: NmiConfig) {
    this.base = config.apiUrl.replace(/\/$/, '');
  }

  // ─── Payment Intents ────────────────────────────────────────────────────────

  createIntent(req: CreatePaymentIntentRequest): Observable<PaymentIntent> {
    return this.http.post<PaymentIntent>(`${this.base}/api/payment-intents`, req);
  }

  getIntent(id: number): Observable<PaymentIntent> {
    return this.http.get<PaymentIntent>(`${this.base}/api/payment-intents/${id}`);
  }

  // ─── Token-based payments (CollectJS) ──────────────────────────────────────

  saleWithToken(req: ConfirmPaymentIntentRequest): Observable<Payment> {
    return this.http.post<Payment>(`${this.base}/api/payments/sale-with-token`, req);
  }

  authWithToken(req: ConfirmPaymentIntentRequest): Observable<Payment> {
    return this.http.post<Payment>(`${this.base}/api/payments/auth-with-token`, req);
  }

  // ─── Vault-based payments ───────────────────────────────────────────────────

  saleWithVault(req: SaleWithVaultRequest): Observable<Payment> {
    return this.http.post<Payment>(`${this.base}/api/payments/sale-with-vault`, req);
  }

  authWithVault(req: AuthWithVaultRequest): Observable<Payment> {
    return this.http.post<Payment>(`${this.base}/api/payments/auth-with-vault`, req);
  }

  // ─── Payment actions ────────────────────────────────────────────────────────

  capture(paymentId: number, req?: CaptureRequest): Observable<Payment> {
    return this.http.post<Payment>(`${this.base}/api/payments/${paymentId}/capture`, req ?? {});
  }

  void(paymentId: number): Observable<Payment> {
    return this.http.post<Payment>(`${this.base}/api/payments/${paymentId}/void`, {});
  }

  refund(paymentId: number, req: RefundRequest): Observable<Refund> {
    return this.http.post<Refund>(`${this.base}/api/payments/${paymentId}/refund`, req);
  }

  // ─── Query ──────────────────────────────────────────────────────────────────

  getPayment(id: number): Observable<Payment> {
    return this.http.get<Payment>(`${this.base}/api/payments/${id}`);
  }
}
